/**
 * Payroll **Runs** — periods, run create/calculate, the two-step approval, and a
 * run's items (team payslips), `/payroll`, WP-W9. `web/shapes/payroll.md` §7 is the
 * agreed shape.
 *
 * Rendered for a session holding any of `payroll.view_payroll` / `run_payroll` /
 * `approve_payroll` / `ceo_approve` (`PayrollScreen` decides that). Within it, four
 * finer-grained, per-row gates that are **both** a status check and a permission
 * check — the test brief's own words: "approve controls gated on the right perms" —
 * because a control that renders but always refuses teaches people to expect
 * refusals (`nav/menu.ts`'s own header makes the identical point about hiding a
 * whole screen):
 *
 *  - **Calculate** — `payroll.run_payroll` AND `status` is `draft` or `calculated`
 *    (`src/modules/payroll/service.ts`'s `CALCULABLE_STATUSES`; a run with any
 *    approval refuses recompute). Since roadmap A-6 the row's control opens
 *    `RunInputsPanel` — the manual inputs of everyone the run pays — and the request is
 *    that panel's own press, so the inputs cannot be skipped by accident (B3).
 *  - **Manager-approve** — `payroll.approve_payroll` AND `status === 'calculated'`.
 *  - **CEO-approve** — `payroll.ceo_approve` AND `status === 'manager_approved'`.
 *    The server additionally refuses when the CEO approver is the same employee as
 *    the manager approver; this client asserts nothing about that beyond letting the
 *    refusal render in place.
 *  - **View items** (a run's team payslips) — `payroll.view_payroll` only, company-wide
 *    once granted (ruling P1: `items-authorizer.ts` gates on that code alone, at company
 *    scope, no employee-tree narrowing) — a holder sees every employee's row in the run;
 *    this client renders whatever page comes back and predicts nothing about its size.
 *
 * **One run write at a time** (fix round 1, R21). The three writes share one screen and
 * `useApiMutation` has no generation guard, so while any of them is in flight every
 * other row's write controls are absent — not disabled — and a line above the list names
 * the run being waited for. Each outcome stays with the run it was pressed for.
 *
 * Periods and runs are company configuration with no employee subject, so — unlike
 * the employee/period ids on the My payslip and Manage sections — creating one takes
 * a plain form, and the run-create form's `period_id`/`template_id` are **typed**
 * rather than picked from the periods/templates lists, because a session holding
 * `payroll.run_payroll` but not `payroll.view_payroll`/`payroll.manage_templates`
 * cannot necessarily read either list to pick from.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  PayrollCalculateRunRequest,
  PayrollItem,
  PayrollPeriod,
  PayrollPeriodCreateRequest,
  PayrollRosterEntry,
  PayrollRun,
  PayrollRunCreateRequest,
} from '../../api/payroll';
import {
  formatMoney,
  PERIOD_STATUS_BADGE,
  PERIOD_STATUS_LABEL,
  RUN_STATUS_PRESENTATION,
} from '../../payroll/status';
import type { InputGrid } from '../../payroll/employee-inputs';
import { RunInputsPanel } from './RunInputsPanel';
import { useCompanyCurrency } from '../../api/company-settings';
import { useSession } from '../../auth/SessionProvider';
import { formatInstant } from '../../i18n/datetime';
import { isUsableReason, MIN_ACCESS_REASON } from '../../access/reason';
import type { PayslipDownloadState } from '../../payroll/payslip-download';
import { PayslipReasonField } from './PayslipReasonField';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Icon, SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

/** The row's own one-press actions. Calculate is not one of them: it opens `RunInputsPanel`. */
export type RunAction = 'manager_approve' | 'ceo_approve';

export interface RunsPanelProps {
  readonly canRunPayroll: boolean;
  readonly canApprove: boolean;
  readonly canCeoApprove: boolean;
  readonly canViewItems: boolean;

  readonly periodsStatus: QueryStatus;
  readonly periods: readonly PayrollPeriod[] | null;
  readonly periodsError: WorkforceApiError | null;
  readonly onReloadPeriods: () => void;
  readonly periodCreatePending: boolean;
  readonly periodCreateError: WorkforceApiError | null;
  readonly periodCreated: PayrollPeriod | null;
  readonly onCreatePeriod: (body: PayrollPeriodCreateRequest) => void;

  readonly runsStatus: QueryStatus;
  readonly runs: readonly PayrollRun[] | null;
  readonly runsError: WorkforceApiError | null;
  readonly onReloadRuns: () => void;
  readonly runsFilterPeriodId: string | null;
  readonly onFilterRuns: (periodId: string | null) => void;
  readonly runCreatePending: boolean;
  readonly runCreateError: WorkforceApiError | null;
  readonly runCreated: PayrollRun | null;
  readonly onCreateRun: (body: PayrollRunCreateRequest) => void;
  /**
   * CS-3: the company's `country_code` names a country the payroll engine has no rules
   * for. Create is DISABLED rather than absent here — unlike the invalid-cell case in
   * `RunInputsPanel`, this is not something the person can fix in this form, so the
   * control stays visible beside the screen's own notice saying why and where to change
   * it. The server refuses the same case with `WORKFORCE_PAYROLL_COUNTRY_UNSUPPORTED`.
   */
  readonly countryUnsupported: boolean;

  /**
   * The run whose write — a calculation or an approval — is in flight, or `null`. One run
   * write at a time (fix round 1, R21): while it is set, every OTHER row's write controls
   * are absent, and the list says which run it is waiting for.
   */
  readonly pendingRunId: string | null;
  /** The run the last approval was aimed at — kept after it settles, so its refusal stays on that row (see `LeaveApprovalsPanel`'s `decidingRequestId` for the same reasoning). */
  readonly approvalRunId: string | null;
  readonly approvalError: WorkforceApiError | null;
  readonly onRunAction: (runId: string, action: RunAction) => void;
  /** Roadmap A-6: the run whose manual-input grid is open, and what the grid needs. */
  readonly inputsRunId: string | null;
  readonly onOpenInputs: (runId: string) => void;
  readonly onCloseInputs: () => void;
  readonly rosterStatus: QueryStatus;
  readonly roster: readonly PayrollRosterEntry[] | null;
  readonly rosterError: WorkforceApiError | null;
  readonly onReloadRoster: () => void;
  readonly inputGrid: InputGrid;
  readonly onInputGridChange: (grid: InputGrid) => void;
  /** The refusal / payslip count of the last calculation of the OPEN run — keyed by the screen. */
  readonly calculateError: WorkforceApiError | null;
  readonly calculatedCount: number | null;
  readonly onCalculate: (runId: string, body: PayrollCalculateRunRequest) => void;

  readonly itemsRunId: string | null;
  readonly onViewItems: (runId: string) => void;
  readonly onCloseItems: () => void;
  readonly itemsStatus: QueryStatus;
  readonly items: readonly PayrollItem[] | null;
  readonly itemsError: WorkforceApiError | null;
  readonly onReloadItems: () => void;
  /** Roadmap U26: the last payslip-PDF download, and how to ask for one team payslip. */
  readonly pdfDownload: PayslipDownloadState;
  readonly onDownloadItemPdf: (run: PayrollRun, item: PayrollItem, reason: string) => void;
}

export function RunsPanel(props: RunsPanelProps): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';

  const [periodLabel, setPeriodLabel] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const periodReady =
    periodLabel.trim() !== '' && periodStart.trim() !== '' && periodEnd.trim() !== '';

  const [filterInput, setFilterInput] = useState('');
  const [runPeriodId, setRunPeriodId] = useState('');
  const [runTemplateId, setRunTemplateId] = useState('');
  const runReady = runPeriodId.trim() !== '' && runTemplateId.trim() !== '';

  /*
   * One box, not two. A company with no periods cannot have a run — the run's own
   * form asks for a period id — so "Chưa có kỳ lương nào" and "Chưa có đợt tính lương
   * nào" are the same fact printed twice, and on a 390px screen they arrived as two
   * near-identical dashed rectangles one scroll apart (finish review, minor f). When
   * both are true the cause is stated once, in the subsection that owns it, and the
   * runs subsection says nothing rather than repeating it.
   */
  const noPeriods = props.periods !== null && props.periods.length === 0;
  const noRuns = props.runs !== null && props.runs.length === 0;
  const nothingAtAll = noPeriods && noRuns;

  return (
    <section className="wp-section" aria-labelledby="w9-runs-heading" data-testid="w9-runs">
      <h2 className="wp-section__title" id="w9-runs-heading">
        {t('payroll.runs.title')}
      </h2>
      <p className="wp-small wp-muted">{t('payroll.runs.intro')}</p>

      {/* ── Periods ─────────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w9-periods">
        <h3 className="wp-section__title">{t('payroll.periods.title')}</h3>

        {props.canRunPayroll ? (
          <form
            className="wp-filters wp-filters--bar"
            onSubmit={(event) => {
              event.preventDefault();
              if (!periodReady || props.periodCreatePending) return;
              props.onCreatePeriod({
                label: periodLabel.trim(),
                period_start: periodStart.trim(),
                period_end: periodEnd.trim(),
              });
            }}
          >
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w9-period-label">
                {t('payroll.periods.create.label')}
              </label>
              <input
                className="wp-field__input"
                id="w9-period-label"
                data-testid="w9-period-label"
                type="text"
                value={periodLabel}
                autoComplete="off"
                onChange={(event) => {
                  setPeriodLabel(event.target.value);
                }}
              />
            </div>
            <DateField
              className="wp-filters__group"
              id="w9-period-start"
              testId="w9-period-start"
              label={t('payroll.periods.create.start')}
              value={periodStart}
              onChange={setPeriodStart}
            />
            <DateField
              className="wp-filters__group"
              id="w9-period-end"
              testId="w9-period-end"
              label={t('payroll.periods.create.end')}
              value={periodEnd}
              onChange={setPeriodEnd}
            />
            <div className="wp-filters__actions">
              <button
                className="wp-button wp-button--primary"
                type="submit"
                data-testid="w9-period-submit"
                disabled={!periodReady || props.periodCreatePending}
              >
                {props.periodCreatePending
                  ? t('payroll.periods.create.submitting')
                  : t('payroll.periods.create.submit')}
              </button>
            </div>
          </form>
        ) : null}
        {props.periodCreateError === null ? null : (
          <ApiErrorNote
            error={props.periodCreateError}
            subject={t('payroll.periods.subject')}
            testId="w9-period-create-error"
          />
        )}
        {props.periodCreated === null ? null : (
          <StatusNote
            severity="low"
            title={t('payroll.periods.create.ok', { label: props.periodCreated.label })}
            testId="w9-period-create-ok"
          />
        )}

        {props.periodsStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('payroll.periods.subject')}
            testId="w9-periods-skeleton"
          />
        ) : null}
        {props.periodsStatus === 'error' && props.periodsError !== null ? (
          <ApiErrorNote
            error={props.periodsError}
            onRetry={props.onReloadPeriods}
            subject={t('payroll.periods.subject')}
            testId="w9-periods-error"
          />
        ) : null}
        {props.periods === null ? null : props.periods.length === 0 ? (
          <EmptyState
            title={t('payroll.periods.empty.title')}
            description={
              nothingAtAll
                ? t('payroll.periods.empty.bodyNothing')
                : t('payroll.periods.empty.body')
            }
            testId="w9-periods-empty"
          />
        ) : (
          <ul className="wp-rows" data-testid="w9-periods-list">
            {props.periods.map((period) => {
              const badge = PERIOD_STATUS_BADGE[period.status];
              return (
                <li className="wp-row" key={period.id} data-testid={`w9-period-${period.id}`}>
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity={badge}
                        icon={SEVERITY_ICON[badge]}
                        label={t(PERIOD_STATUS_LABEL[period.status])}
                        testId={`w9-period-status-${period.id}`}
                      />{' '}
                      <span>{period.label}</span>
                    </p>
                    <p className="wp-small wp-muted">
                      {t('payroll.periods.row.range', {
                        start: period.period_start,
                        end: period.period_end,
                      })}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Runs ────────────────────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w9-runs-list-section">
        <h3 className="wp-section__title">{t('payroll.runsList.title')}</h3>

        <form
          className="wp-filters wp-filters--bar"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = filterInput.trim();
            props.onFilterRuns(trimmed === '' ? null : trimmed);
          }}
        >
          {/* The periods this screen has already read, offered as a list rather than
              asked for as a typed id (finish review, M2). `props.periods` is `null`
              only while the read is in flight or after it was refused, and then the
              box stays a box — the same honest fallback `CreateApplicationForm` uses
              for a requisition it cannot list. */}
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w9-runs-filter-period">
              {t('payroll.runsList.filterPeriod')}
            </label>
            {props.periods === null ? (
              <input
                className="wp-field__input"
                id="w9-runs-filter-period"
                data-testid="w9-runs-filter-period"
                type="text"
                inputMode="numeric"
                value={filterInput}
                autoComplete="off"
                onChange={(event) => {
                  setFilterInput(event.target.value);
                }}
              />
            ) : (
              <div className="wp-select">
                <select
                  className="wp-field__input"
                  id="w9-runs-filter-period"
                  data-testid="w9-runs-filter-period"
                  value={filterInput}
                  onChange={(event) => {
                    setFilterInput(event.target.value);
                  }}
                >
                  <option value="">{t('payroll.periods.any')}</option>
                  {props.periods.map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.label}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down" className="wp-select__chevron" />
              </div>
            )}
            <p className="wp-small wp-muted">{t('payroll.runsList.filterPeriodHint')}</p>
          </div>
          <div className="wp-filters__actions">
            <button className="wp-button" type="submit" data-testid="w9-runs-filter-apply">
              {t('payroll.runsList.filterApply')}
            </button>
          </div>
        </form>

        {props.canRunPayroll ? (
          <form
            className="wp-filters wp-filters--bar"
            onSubmit={(event) => {
              event.preventDefault();
              if (!runReady || props.runCreatePending || props.countryUnsupported) return;
              props.onCreateRun({
                period_id: runPeriodId.trim(),
                template_id: runTemplateId.trim(),
              });
            }}
          >
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w9-run-period-id">
                {props.periods === null
                  ? t('payroll.periodIdLabel')
                  : t('payroll.periods.pickLabel')}
              </label>
              {props.periods === null ? (
                <input
                  className="wp-field__input"
                  id="w9-run-period-id"
                  data-testid="w9-run-period-id"
                  type="text"
                  inputMode="numeric"
                  value={runPeriodId}
                  autoComplete="off"
                  onChange={(event) => {
                    setRunPeriodId(event.target.value);
                  }}
                />
              ) : (
                <div className="wp-select">
                  <select
                    className="wp-field__input"
                    id="w9-run-period-id"
                    data-testid="w9-run-period-id"
                    value={runPeriodId}
                    onChange={(event) => {
                      setRunPeriodId(event.target.value);
                    }}
                  >
                    <option value="">{t('payroll.periods.choose')}</option>
                    {props.periods.map((period) => (
                      <option key={period.id} value={period.id}>
                        {period.label}
                      </option>
                    ))}
                  </select>
                  <Icon name="chevron-down" className="wp-select__chevron" />
                </div>
              )}
            </div>
            <div className="wp-filters__group">
              <label className="wp-field__label" htmlFor="w9-run-template-id">
                {t('payroll.templateIdLabel')}
              </label>
              <input
                className="wp-field__input"
                id="w9-run-template-id"
                data-testid="w9-run-template-id"
                type="text"
                inputMode="numeric"
                value={runTemplateId}
                autoComplete="off"
                onChange={(event) => {
                  setRunTemplateId(event.target.value);
                }}
              />
            </div>
            <div className="wp-filters__actions">
              <button
                className="wp-button wp-button--primary"
                type="submit"
                data-testid="w9-run-submit"
                disabled={!runReady || props.runCreatePending || props.countryUnsupported}
              >
                {props.runCreatePending
                  ? t('payroll.runsList.create.submitting')
                  : t('payroll.runsList.create.submit')}
              </button>
            </div>
          </form>
        ) : null}
        {props.runCreateError === null ? null : (
          <ApiErrorNote
            error={props.runCreateError}
            subject={t('payroll.runsList.subject')}
            testId="w9-run-create-error"
          />
        )}
        {props.runCreated === null ? null : (
          <StatusNote
            severity="low"
            title={t('payroll.runsList.create.ok', { id: props.runCreated.id })}
            testId="w9-run-create-ok"
          />
        )}

        {props.runsStatus === 'loading' ? (
          <Skeleton
            rows={2}
            variant="block"
            label={t('payroll.runsList.subject')}
            testId="w9-runs-skeleton"
          />
        ) : null}
        {props.runsStatus === 'error' && props.runsError !== null ? (
          <ApiErrorNote
            error={props.runsError}
            onRetry={props.onReloadRuns}
            subject={t('payroll.runsList.subject')}
            testId="w9-runs-error"
          />
        ) : null}
        {props.pendingRunId === null ? null : (
          <p className="wp-small" data-testid="w9-runs-write-pending">
            {t('payroll.runsList.writePending', { id: props.pendingRunId })}
          </p>
        )}
        {props.runs === null || nothingAtAll ? null : props.runs.length === 0 ? (
          <EmptyState
            title={t('payroll.runsList.empty.title')}
            description={t('payroll.runsList.empty.body')}
            testId="w9-runs-empty"
          />
        ) : (
          <ul className="wp-records" data-testid="w9-runs-rows">
            {props.runs.map((run) => {
              const presentation = RUN_STATUS_PRESENTATION[run.status];
              /** This row's own write is in flight: its controls show the working state. */
              const acting = props.pendingRunId === run.id;
              /** Another row's write is in flight: this row offers no write at all (R21). */
              const elsewhere = props.pendingRunId !== null && !acting;
              const rowError = props.approvalRunId === run.id ? props.approvalError : null;
              const canCalculate =
                props.canRunPayroll && (run.status === 'draft' || run.status === 'calculated');
              const canManagerApprove = props.canApprove && run.status === 'calculated';
              const canCeoApprove = props.canCeoApprove && run.status === 'manager_approved';
              const inputsOpen = props.inputsRunId === run.id;
              /*
                R20: the panel stays while it holds this run's calculation outcome, even once
                a re-read shows the run approved — a refusal that vanished one GET after it
                arrived would read as a calculation that went through.
              */
              const holdsOutcome = props.calculateError !== null || props.calculatedCount !== null;
              return (
                <li className="wp-record" key={run.id} data-testid={`w9-run-${run.id}`}>
                  <div className="wp-record__head">
                    <div className="wp-record__main">
                      <p>
                        <Badge
                          severity={presentation.severity}
                          icon={presentation.icon}
                          label={t(presentation.labelKey)}
                          testId={`w9-run-status-${run.id}`}
                        />{' '}
                        <span>{t('payroll.runsList.row.id', { id: run.id })}</span>
                      </p>
                      <p className="wp-small wp-muted">
                        {t('payroll.runsList.row.meta', {
                          periodId: run.period_id,
                          templateId: run.template_id,
                        })}
                      </p>
                    </div>
                    <div className="wp-record__aside">
                      <div className="wp-record__actions">
                        {canCalculate && !elsewhere ? (
                          <button
                            className="wp-button"
                            type="button"
                            data-testid={`w9-calculate-${run.id}`}
                            disabled={acting}
                            onClick={() => {
                              props.onOpenInputs(run.id);
                            }}
                          >
                            {acting
                              ? t('payroll.runsList.working')
                              : t('payroll.runsList.calculate')}
                          </button>
                        ) : null}
                        {canManagerApprove && !elsewhere ? (
                          <button
                            className="wp-button wp-button--primary"
                            type="button"
                            data-testid={`w9-manager-approve-${run.id}`}
                            disabled={acting}
                            onClick={() => {
                              props.onRunAction(run.id, 'manager_approve');
                            }}
                          >
                            {acting
                              ? t('payroll.runsList.working')
                              : t('payroll.runsList.managerApprove')}
                          </button>
                        ) : null}
                        {canCeoApprove && !elsewhere ? (
                          <button
                            className="wp-button wp-button--primary"
                            type="button"
                            data-testid={`w9-ceo-approve-${run.id}`}
                            disabled={acting}
                            onClick={() => {
                              props.onRunAction(run.id, 'ceo_approve');
                            }}
                          >
                            {acting
                              ? t('payroll.runsList.working')
                              : t('payroll.runsList.ceoApprove')}
                          </button>
                        ) : null}
                        {props.canViewItems ? (
                          <button
                            className="wp-button"
                            type="button"
                            data-testid={`w9-view-items-${run.id}`}
                            onClick={() => {
                              props.onViewItems(run.id);
                            }}
                          >
                            {t('payroll.runsList.viewItems')}
                          </button>
                        ) : null}
                      </div>
                      {rowError === null ? null : (
                        <ApiErrorNote
                          error={rowError}
                          subject={t('payroll.runsList.subject')}
                          testId={`w9-run-action-error-${run.id}`}
                        />
                      )}
                    </div>
                  </div>
                  {/* The two signatures a payroll run is released by, printed as the two
                      ruled lines the paper voucher this replaces had at its foot. Read
                      off `manager_approved_by/at` and `ceo_approved_by/at` rather than
                      off the status word, so the strip cannot show a signature the
                      record does not hold. Always present, even on a draft: an empty
                      dashed rule is the honest picture of an unsigned voucher, and it is
                      what tells a manager at a glance which of the two is outstanding. */}
                  <div
                    className="wp-signatures"
                    role="group"
                    aria-label={t('payroll.approval.groupLabel', { id: run.id })}
                    data-testid={`w9-run-signatures-${run.id}`}
                  >
                    <Signature
                      label={t('payroll.approval.manager')}
                      who={run.manager_approved_by}
                      when={run.manager_approved_at}
                      timezone={timezone}
                      testId={`w9-run-sig-manager-${run.id}`}
                    />
                    <Signature
                      label={t('payroll.approval.ceo')}
                      who={run.ceo_approved_by}
                      when={run.ceo_approved_at}
                      timezone={timezone}
                      testId={`w9-run-sig-ceo-${run.id}`}
                    />
                  </div>
                  {/* Beside the figure column, not in it: that column is `white-space: nowrap`,
                      and this panel is sentences and a grid. It takes the row's full width. */}
                  {inputsOpen && (canCalculate || holdsOutcome) ? (
                    <RunInputsPanel
                      runId={run.id}
                      calculable={canCalculate}
                      rosterStatus={props.rosterStatus}
                      roster={props.roster}
                      rosterError={props.rosterError}
                      onReloadRoster={props.onReloadRoster}
                      grid={props.inputGrid}
                      onGridChange={props.onInputGridChange}
                      pendingRunId={props.pendingRunId}
                      calculateError={props.calculateError}
                      calculatedCount={props.calculatedCount}
                      onCalculate={(body) => {
                        props.onCalculate(run.id, body);
                      }}
                      onClose={props.onCloseInputs}
                    />
                  ) : null}
                  {/*
                    R16 (controller ruling): likewise beside the figure column, not in it.
                    The team payslips' PDF reason field is a sentence and an input, not the
                    short `nowrap` employee/net lines the figure column was sized for, so
                    `RunItems` — reason field, list and all — moved out here with
                    `RunInputsPanel` rather than staying nested in `wp-row__figure`. The
                    "View team payslips" trigger button stays in the figure column, in
                    `wp-toggle`, exactly as Calculate's button stays there while
                    `RunInputsPanel` (what it opens) is a full-width sibling.
                  */}
                  {props.itemsRunId === run.id ? (
                    <RunItems
                      status={props.itemsStatus}
                      items={props.items}
                      error={props.itemsError}
                      onReload={props.onReloadItems}
                      onClose={props.onCloseItems}
                      released={run.status === 'ceo_approved' || run.status === 'paid'}
                      pdfDownload={props.pdfDownload}
                      onDownloadPdf={(item, reason) => {
                        props.onDownloadItemPdf(run, item, reason);
                      }}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * One of the two signature lines under a run.
 *
 * `who` is an employee id, so it is mono — an auditor reconciling a release against
 * `wp_audit_log` retypes it. The unsigned branch is **words** ("chưa ký"), never a
 * blank line and never a dash: on a two-signature release, "nobody has signed this
 * yet" is the fact the reader came for, and a blank reads as a rendering bug.
 *
 * `data-signed` carries the discriminant so a test cannot be satisfied by the
 * neighbouring signature's text.
 */
function Signature({
  label,
  who,
  when,
  timezone,
  testId,
}: {
  readonly label: string;
  readonly who: string | null;
  readonly when: string | null;
  readonly timezone: string;
  readonly testId: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const signed = typeof who === 'string' && who !== '';
  return (
    <div
      className={signed ? 'wp-signature' : 'wp-signature wp-signature--unsigned'}
      data-testid={testId}
      data-signed={signed ? 'true' : 'false'}
    >
      <span className="wp-signature__label">{label}</span>
      <span className="wp-signature__who">
        {signed ? <code className="wp-mono">{who}</code> : t('payroll.approval.unsigned')}
      </span>
      {when === null ? null : (
        <span className="wp-signature__when">{formatInstant(when, locale, timezone)}</span>
      )}
    </div>
  );
}

function RunItems({
  status,
  items,
  error,
  onReload,
  onClose,
  released,
  pdfDownload,
  onDownloadPdf,
}: {
  readonly status: QueryStatus;
  readonly items: readonly PayrollItem[] | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly onClose: () => void;
  /**
   * `ceo_approved` or `paid` — the only runs whose payslips the PDF route releases
   * (`readItemForRunAndEmployee`). Any other run offers no PDF control at all.
   */
  readonly released: boolean;
  readonly pdfDownload: PayslipDownloadState;
  readonly onDownloadPdf: (item: PayrollItem, reason: string) => void;
}): ReactNode {
  const { t } = useI18n();
  // The company's own unit (Task CS-2), not a VND this bundle assumed.
  const currency = useCompanyCurrency();
  const [reason, setReason] = useState('');
  const usable = isUsableReason(reason);
  const requesting = pdfDownload.status === 'requesting';
  /** The last download's outcome, shown only beside the payslip it was for. */
  const outcomeFor = (itemId: string): PayslipDownloadState =>
    pdfDownload.status !== 'idle' && pdfDownload.key === itemId ? pdfDownload : { status: 'idle' };

  return (
    <div className="wp-section__subsection" data-testid="w9-run-items">
      {status === 'loading' ? (
        <Skeleton
          rows={2}
          variant="block"
          label={t('payroll.runItems.subject')}
          testId="w9-run-items-skeleton"
        />
      ) : null}
      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={onReload}
          subject={t('payroll.runItems.subject')}
          testId="w9-run-items-error"
        />
      ) : null}
      {items === null ? null : items.length === 0 ? (
        <EmptyState
          title={t('payroll.runItems.empty.title')}
          description={t('payroll.runItems.empty.body')}
          testId="w9-run-items-empty"
        />
      ) : (
        <>
          {released ? (
            <PayslipReasonField
              value={reason}
              onChange={setReason}
              testId="w9-run-items-pdf-reason"
            />
          ) : (
            <p className="wp-small wp-muted" data-testid="w9-run-items-pdf-unreleased">
              {t('payroll.pdf.unreleased')}
            </p>
          )}
          <ul className="wp-rows" data-testid="w9-run-items-list">
            {items.map((item) => {
              const outcome = outcomeFor(item.id);
              return (
                <li className="wp-row" key={item.id} data-testid={`w9-run-item-${item.id}`}>
                  <div className="wp-row__main">
                    <p>{t('payroll.runItems.row.employee', { employeeId: item.employee_id })}</p>
                    {/* Net moved out of the name column and into the figure column, in
                        `formatMoney` grouping and tabular figures: thirty payslips whose
                        net pay is set in prose under a name cannot be compared down the
                        page, which is exactly what Luật Số Thẳng Cột is about. */}
                    {outcome.status === 'refused' ? (
                      <ApiErrorNote
                        error={outcome.error}
                        subject={t('payroll.pdf.subject')}
                        unavailableNote={t('payroll.pdf.notFound')}
                        testId={`w9-run-item-pdf-error-${item.id}`}
                      />
                    ) : null}
                    {outcome.status === 'delivered' ? (
                      <StatusNote
                        severity="low"
                        title={t('payroll.pdf.delivered', { filename: outcome.filename })}
                        testId={`w9-run-item-pdf-ok-${item.id}`}
                      />
                    ) : null}
                  </div>
                  <div className="wp-row__figure">
                    <p className="wp-num" data-testid={`w9-run-item-net-${item.id}`}>
                      {t('payroll.runItems.row.net', {
                        net: formatMoney(Number(item.net), currency),
                      })}
                    </p>
                    {released ? (
                      <button
                        className="wp-button"
                        type="button"
                        data-testid={`w9-run-item-pdf-${item.id}`}
                        disabled={!usable || requesting}
                        onClick={() => {
                          onDownloadPdf(item, reason);
                        }}
                      >
                        {outcome.status === 'requesting'
                          ? t('payroll.pdf.downloading')
                          : t('payroll.pdf.download')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          {released && !usable ? (
            <p className="wp-small wp-muted" data-testid="w9-run-items-pdf-reason-needed">
              {t('payroll.pdf.reasonNeeded', { min: MIN_ACCESS_REASON })}
            </p>
          ) : null}
        </>
      )}
      <div className="wp-filters__actions">
        <button
          className="wp-button"
          type="button"
          data-testid="w9-run-items-close"
          onClick={onClose}
        >
          {t('payroll.runItems.close')}
        </button>
      </div>
    </div>
  );
}
